import type {
  CameraTrackingStartOptions,
  EngineLoadState,
  NormalizedScreenPoint,
  PlacementPose,
  RuntimeAdapter,
  RuntimePhase,
  RuntimeSnapshot,
  RuntimeStopReason,
  RuntimeSupport,
  TrackingState,
} from "@/runtime/types";
import {
  attachThreeGlobal,
  clamp01,
  clearThreeGlobal,
  errorMessage,
  loadEighthWallEngine,
  selectBestHit,
  tuple3,
  tuple4,
  type EighthWallEngine,
  type PipelineModule,
  type PipelineUpdateEvent,
} from "@/runtime/eighthWallEngine";

interface EighthWallRuntimeOptions {
  load?: () => Promise<EighthWallEngine>;
  engineUrl?: string;
}

export class EighthWallRuntime implements RuntimeAdapter {
  readonly mode = "eighth-wall" as const;
  private readonly load: () => Promise<EighthWallEngine>;
  private enginePromise: Promise<EighthWallEngine> | null = null;
  private engine: EighthWallEngine | null = null;
  private modules: PipelineModule[] = [];
  private bridge: Awaited<ReturnType<CameraTrackingStartOptions["mountScene"]>> | null = null;
  private listeners = new Set<(snapshot: RuntimeSnapshot) => void>();
  private sessionToken = 0;
  private running = false;
  private snapshot: RuntimeSnapshot = {
    mode: "eighth-wall",
    phase: "idle",
    placement: null,
    error: null,
    engine: "idle",
    tracking: "unavailable",
  };

  constructor(options: EighthWallRuntimeOptions = {}) {
    this.load = options.load ?? (() => loadEighthWallEngine(options.engineUrl));
  }

  async probe(): Promise<RuntimeSupport> {
    const supported = typeof window !== "undefined" && window.isSecureContext;
    return supported ? { supported: true } : { supported: false, reason: "secure-context-required" };
  }

  async preload(): Promise<void> {
    if (!this.enginePromise) {
      this.update({ phase: "loading-engine", engine: "prefetching", error: null });
      this.enginePromise = this.load().then(async (engine) => {
        await engine.loadChunk?.("slam");
        this.engine = engine;
        this.update({ phase: "idle", engine: "ready" });
        return engine;
      }).catch((cause: unknown) => {
        this.enginePromise = null;
        this.update({ phase: "error", engine: "failed", error: errorMessage(cause, "8th Wall could not be loaded.") });
        throw cause;
      });
    }
    await this.enginePromise;
  }

  async start(options?: CameraTrackingStartOptions): Promise<void> {
    const token = ++this.sessionToken;
    await this.preload();
    if (token !== this.sessionToken) return;
    const engine = this.engine;
    if (!engine) throw new Error("8th Wall did not initialize.");

    this.update({ phase: "requesting-permission", tracking: "initializing", error: null });
    this.running = true;

    if (!engine.run && engine.start) {
      try {
        await engine.start();
        if (token !== this.sessionToken && this.running) await this.teardownEngine(engine, true);
        else this.update({ phase: "searching-surface", tracking: "normal" });
      } catch (cause) {
        await this.teardownEngine(engine, true);
        this.update({ phase: "error", tracking: "unavailable", error: errorMessage(cause, "The SLAM session could not start.") });
        throw cause;
      }
      return;
    }

    if (!options || !engine.run || !engine.GlTextureRenderer || !engine.Threejs || !engine.XrController) {
      this.running = false;
      throw new Error("The 8th Wall camera pipeline is incomplete.");
    }

    attachThreeGlobal();
    engine.XrController.configure({
      disableWorldTracking: false,
      enableLighting: false,
      enableWorldPoints: false,
      scale: "absolute",
    });

    const appModule: PipelineModule = {
      name: "rising-pixel-r3f-bridge",
      onStart: () => { void this.mountBridge(engine, options, token); },
      onCameraStatusChange: ({ status, error }) => {
        if (token !== this.sessionToken) return;
        if (status === "failed") {
          this.update({ phase: "error", tracking: "unavailable", error: errorMessage(error, "The camera could not start.") });
        } else if (status === "hasVideo" || status === "hasStream") {
          this.update({ phase: "tracking-initializing", tracking: "initializing" });
        }
      },
      onUpdate: (event: PipelineUpdateEvent) => {
        if (token !== this.sessionToken) return;
        const reality = event.processCpuResult?.reality;
        this.applyTrackingState(reality?.trackingStatus, reality?.trackingReason);
        this.bridge?.advance(event.frameStartResult?.timestamp ?? performance.now());
      },
    };

    this.modules = [
      engine.GlTextureRenderer.pipelineModule(),
      engine.Threejs.pipelineModule(),
      engine.XrController.pipelineModule(),
      appModule,
    ];
    engine.addCameraPipelineModules?.(this.modules);
    try {
      await engine.run({ canvas: options.canvas, allowedDevices: engine.XrConfig?.device().ANY });
      if (token !== this.sessionToken && this.running) await this.teardownEngine(engine, false);
    } catch (cause) {
      await this.teardownEngine(engine, false);
      this.update({ phase: "error", tracking: "unavailable", error: errorMessage(cause, "The SLAM session could not start.") });
      throw cause;
    }
  }

  async requestPlacement(point: NormalizedScreenPoint): Promise<PlacementPose | null> {
    const engine = this.engine;
    if (!engine || !this.running || this.snapshot.tracking === "limited" || this.snapshot.tracking === "initializing") return null;
    const token = this.sessionToken;
    const clamped = { x: clamp01(point.x), y: clamp01(point.y) };
    const raw = engine.hitTest ? await engine.hitTest(clamped) : engine.XrController?.hitTest(clamped.x, clamped.y) ?? [];
    if (token !== this.sessionToken || !this.running) return null;
    const results = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const selected = selectBestHit(results);
    if (!selected) return null;
    return { position: tuple3(selected.position), quaternion: tuple4(selected.quaternion ?? selected.rotation), scale: selected.scale ?? 1 };
  }

  async hitTest(point: NormalizedScreenPoint): Promise<PlacementPose | null> {
    return this.requestPlacement(point);
  }

  async pause(): Promise<void> {
    if (!this.running) return;
    await this.engine?.pause?.();
    this.update({ phase: "paused", tracking: "paused" });
  }

  async resume(): Promise<void> {
    if (!this.running) return;
    await this.engine?.resume?.();
    this.update({ phase: "tracking-initializing", tracking: "initializing" });
  }

  async reposition(): Promise<void> {
    this.engine?.XrController?.recenter();
    this.update({ placement: null, phase: "searching-surface" });
  }

  acceptPlacement(pose: PlacementPose): void {
    if (!this.running) return;
    this.update({ placement: pose, phase: "placed" });
  }

  async stop(reason: RuntimeStopReason): Promise<void> {
    void reason;
    ++this.sessionToken;
    const engine = this.engine;
    if (engine) await this.teardownEngine(engine, true);
    this.update({ phase: "stopped", tracking: "unavailable", placement: null, error: null });
  }

  getSnapshot(): RuntimeSnapshot { return this.snapshot; }

  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  private async mountBridge(engine: EighthWallEngine, options: CameraTrackingStartOptions, token: number): Promise<void> {
    try {
      const xrScene = engine.Threejs?.xrScene();
      if (!xrScene) throw new Error("8th Wall did not expose its Three.js scene.");
      const bridge = await options.mountScene({ renderer: xrScene.renderer, camera: xrScene.camera, parentScene: xrScene.scene });
      if (token !== this.sessionToken) {
        bridge.unmount();
        return;
      }
      this.bridge = bridge;
      engine.XrController?.updateCameraProjectionMatrix?.({ origin: xrScene.camera.position, facing: xrScene.camera.quaternion });
      this.update({ phase: "tracking-initializing", tracking: "initializing" });
    } catch (cause) {
      if (token === this.sessionToken) this.update({ phase: "error", tracking: "unavailable", error: errorMessage(cause, "The 3D scene could not attach to 8th Wall.") });
    }
  }

  private async teardownEngine(engine: EighthWallEngine, destroyFacade: boolean): Promise<void> {
    const wasRunning = this.running;
    this.running = false;
    this.bridge?.unmount();
    this.bridge = null;
    for (const module of [...this.modules].reverse()) engine.removeCameraPipelineModule?.(module.name);
    this.modules = [];
    if (wasRunning) await engine.stop?.();
    if (wasRunning && destroyFacade) await engine.destroy?.();
    clearThreeGlobal();
  }

  private applyTrackingState(status?: string, reason?: string): void {
    if (reason === "INITIALIZING") {
      this.update({ phase: "tracking-initializing", tracking: "initializing" });
      return;
    }
    if (status === "LIMITED") {
      this.update({ phase: "tracking-limited", tracking: "limited" });
      return;
    }
    if (status === "NORMAL") this.update({ phase: "searching-surface", tracking: "normal" });
  }

  private update(patch: Partial<RuntimeSnapshot> & { engine?: EngineLoadState; tracking?: TrackingState; phase?: RuntimePhase }): void {
    const next = { ...this.snapshot, ...patch };
    if (JSON.stringify(next) === JSON.stringify(this.snapshot)) return;
    this.snapshot = next;
    for (const listener of this.listeners) listener(this.snapshot);
  }
}
